/* global React */
// Path-based AR localization for hardcoded UI labels (dossier content comes
// from content/vdc_ar.js). EN strings are unchanged.
function dzIsAr() { return typeof window !== 'undefined' && window.location.pathname.startsWith('/ar/'); }
function dzT(en, ar) { return dzIsAr() ? ar : en; }
function dzVdcHref() { return dzIsAr() ? '/ar/vdc/' : '/vdc/'; }
/* ──────────────────────────────────────────────────────────────────────
   VdcCasePage — /vdc/<slug>/
   Slug-driven case dossier for VDC immersive projects.
   Reads window.location.pathname (matched against /vdc/<slug>/) and
   renders the matching item from window.DZ_VDC.projects.items.

   Sections:
     ▸ Reading progress bar (fixed top)
     ▸ Cinematic parallax hero (image OR video OR space SVG)
     ▸ The brief — story narrative
     ▸ Key facts grid
     ▸ Approach — numbered scope bullets
     ▸ Tools — hardware / devices used (with notes)
     ▸ Software stack — chips
     ▸ Gallery (Al Arabi only — 4 themes)
     ▸ Related cases — 3 sibling immersive projects
     ▸ Floating "Discuss your project" CTA after first viewport

   Namespace: .dz-vdc-case-*  (matches .dz-detail-* in spirit)
   ────────────────────────────────────────────────────────────────────── */

const { useState: useVdcCsState, useEffect: useVdcCsEffect, useRef: useVdcCsRef } = React;

/* Swap auto-loading hero videos to their -mobile.mp4 variants on
   viewports ≤800px. Use only for autoplay-loop hero videos; keep
   desktop quality for the play-controlled case video. */
function dzMobileVideo(url) {
  if (typeof window === 'undefined' || !url) return url;
  if (!window.matchMedia || !window.matchMedia('(max-width: 800px)').matches) return url;
  return url.replace(/(\.mp4)(\?[^"'\s]*)?$/i, '-mobile$1$2');
}

/* ─── Reading progress bar ─── */
function VdcCaseProgress() {
  const [progress, setProgress] = useVdcCsState(0);
  useVdcCsEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const scrolled = window.scrollY;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const pct = max > 0 ? Math.min(100, (scrolled / max) * 100) : 0;
        setProgress(pct);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  return (
    <div className="dz-vdc-case__progress" aria-hidden="true">
      <div className="dz-vdc-case__progress-bar" style={{ width: progress + '%' }} />
    </div>
  );
}

/* ─── Floating CTA (appears after first viewport) ─── */
function VdcCaseFloatingCta() {
  const [visible, setVisible] = useVdcCsState(false);
  useVdcCsEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setVisible(window.scrollY > window.innerHeight * 0.7);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  return (
    <a
      href="#inquiry"
      className={'dz-vdc-case__floating-cta' + (visible ? ' is-visible' : '')}
      aria-label={dzT('Discuss your immersive project', 'ناقش مشروعك الغامر')}
    >
      <span>{dzT('Discuss your project', 'ناقش مشروعك')}</span>
      <span aria-hidden="true">→</span>
    </a>
  );
}

/* ─── Reveal-on-scroll wrapper ─── */
function VdcCaseReveal({ children, className }) {
  const [visible, setVisible] = useVdcCsState(false);
  const ref = useVdcCsRef(null);
  useVdcCsEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section
      ref={ref}
      className={(className || '') + ' dz-vdc-case__reveal' + (visible ? ' is-visible' : '')}
    >
      {children}
    </section>
  );
}

/* ─── Space SVG hero (for the in-production Space Hologram case) ─── */
function VdcCaseSpaceHero() {
  return (
    <svg className="dz-vdc-case__hero-svg" viewBox="0 0 1600 900"
         preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <radialGradient id="vdcCaseSpace" cx="50%" cy="50%" r="80%">
          <stop offset="0%" stopColor="rgba(79,200,244,0.20)" />
          <stop offset="60%" stopColor="rgba(43,61,79,1)" />
          <stop offset="100%" stopColor="rgba(26,45,62,1)" />
        </radialGradient>
      </defs>
      <rect width="1600" height="900" fill="url(#vdcCaseSpace)" />
      {Array.from({ length: 90 }).map((_, i) => {
        const x = (i * 71) % 1600;
        const y = (i * 53) % 900;
        const r = 0.7 + (i % 4) * 0.4;
        return (
          <circle key={i} cx={x} cy={y} r={r} fill="white" opacity={0.3 + (i % 5) * 0.13}>
            <animate attributeName="opacity" values="0.1;0.85;0.1"
                     dur={`${2 + (i % 4)}s`} begin={`${(i * 0.07) % 3}s`}
                     repeatCount="indefinite" />
          </circle>
        );
      })}
      <g transform="translate(800 450)" stroke="#4FC8F4" strokeWidth="1" fill="none">
        <ellipse cx="0" cy="0" rx="320" ry="90"  opacity="0.32" />
        <ellipse cx="0" cy="0" rx="460" ry="140" opacity="0.22" transform="rotate(-15)" />
        <ellipse cx="0" cy="0" rx="600" ry="200" opacity="0.16" transform="rotate(15)" />
      </g>
      <g transform="translate(800 450)">
        <circle r="120" fill="#4FC8F4" opacity="0.18" />
        <circle r="80"  fill="#4FC8F4" opacity="0.85" />
        <ellipse cx="0" cy="0" rx="200" ry="22" fill="none" stroke="#4FC8F4"
                 strokeWidth="1.2" opacity="0.65" transform="rotate(-12)" />
      </g>
      <circle r="9" fill="#4FC8F4">
        <animateMotion dur="14s" repeatCount="indefinite"
                       path="M 1080 450 a 320 90 0 1 1 -640 0 a 320 90 0 1 1 640 0" />
      </circle>
    </svg>
  );
}

/* ─── Parallax hero ─── */
function VdcCaseHero({ item, detail }) {
  const [scrollY, setScrollY] = useVdcCsState(0);
  useVdcCsEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const y = Math.min(window.innerHeight, window.scrollY);
        setScrollY(y);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const imageStyle = {
    transform: 'translate3d(0,' + (scrollY * 0.3) + 'px, 0) scale(' +
               (1 + Math.min(0.06, scrollY * 0.0002)) + ')'
  };

  const useVideo  = !!detail.heroVideo;
  const useImage  = !!detail.heroImage && !useVideo;
  const useSymbol = !useVideo && !useImage && detail.heroSymbol === 'space';

  return (
    <div className="dz-vdc-case__hero">
      <div className="dz-vdc-case__hero-image-wrap" style={imageStyle}>
        {useVideo ? (
          <video
            src={dzMobileVideo(detail.heroVideo)}
            autoPlay muted loop playsInline preload="metadata"
            disablePictureInPicture disableRemotePlayback
            controlsList="nodownload nofullscreen noremoteplayback"
            aria-label={item.title}
          />
        ) : useImage ? (
          <img src={detail.heroImage} alt={item.title} loading="eager" decoding="async" />
        ) : useSymbol ? (
          <VdcCaseSpaceHero />
        ) : null}
      </div>

      <div className="dz-vdc-case__hero-shade" aria-hidden="true" />

      <div className="dz-vdc-case__hero-content">
        <a href={dzVdcHref()} className="dz-vdc-case__back">
          <span aria-hidden="true">←</span>
          <span>{dzT('All immersive work', 'كل الأعمال الغامرة')}</span>
        </a>

        <div className="dz-vdc-case__hero-text">
          <span className="dz-vdc-case__hero-cat">
            {item.kicker}{detail.year ? ' · ' + detail.year : ''}
          </span>
          <h1 className="dz-vdc-case__hero-title">{item.title}</h1>
          <p className="dz-vdc-case__hero-meta">
            {detail.location ? detail.location : ''}
            {item.client ? <><span className="dz-vdc-case__sep">·</span>{item.client}</> : null}
            {detail.status ? <><span className="dz-vdc-case__sep">·</span><em>{detail.status}</em></> : null}
          </p>
          {item.tags && item.tags.length ? (
            <div className="dz-vdc-case__hero-tags">
              {item.tags.map((t) => (
                <span key={t} className="dz-vdc-case__chip">{t}</span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ─── Al Arabi 4-theme gallery (only renders if media.type === 'gallery') ─── */
function VdcCaseGallery({ shots }) {
  if (!shots || !shots.length) return null;
  return (
    <div className="dz-vdc-case__gallery">
      {shots.map((s, i) => (
        <figure className="dz-vdc-case__gallery-cell" key={i}
                style={{ transitionDelay: (i * 80) + 'ms' }}>
          {s.src ? (
            <img src={s.src} alt={s.alt} loading="lazy" decoding="async" />
          ) : (
            <div className="dz-vdc-case__gallery-pending" aria-hidden="true">
              <span className="dz-vdc-case__gallery-pending-mark">+</span>
              <span className="dz-vdc-case__gallery-pending-text">{dzT('In production', 'قيد الإنتاج')}</span>
            </div>
          )}
          <figcaption className="dz-vdc-case__gallery-caption">{s.label}</figcaption>
        </figure>
      ))}
    </div>
  );
}

/* ─── Related cases — editorial typographic "Index" ─── */
/* Compact, no big media. Reads like a book's "Other works" page —
   numbered rows on a light aqua-wash band. Each row reveals a
   thin aqua underline + arrow translation on hover. */
function VdcCaseRelated({ all, currentSlug }) {
  const idx = all.findIndex((p) => p.slug === currentSlug);
  if (idx === -1) return null;
  const total = all.length;
  // Order by sequence in items[], wrapping after the current case.
  const others = [];
  for (let k = 1; k < total; k++) {
    others.push(all[(idx + k) % total]);
  }
  if (!others.length) return null;

  return (
    <section className="dz-vdc-case__related">
      <div className="dz-vdc-case__related-inner">

        <header className="dz-vdc-case__related-head">
          <span className="dz-vdc-case__related-eyebrow">
            <span className="dz-vdc-case__related-eyebrow-dot" />
            {dzT('More immersive work', 'المزيد من الأعمال الغامرة')}
          </span>
          <a href={dzVdcHref()} className="dz-vdc-case__related-all">
            <span>{dzT('See all', 'عرض الكل')}</span>
            <span aria-hidden="true">→</span>
          </a>
        </header>

        <ol className="dz-vdc-case__related-list">
          {others.map((r, i) => (
            <li
              key={r.slug}
              className="dz-vdc-case__related-item"
              style={{ transitionDelay: (i * 60) + 'ms' }}
            >
              <a
                href={r.caseLink || '/vdc/'}
                className="dz-vdc-case__related-row"
                aria-label={dzT('Open case — ', 'افتح الحالة — ') + r.title}
              >
                <span className="dz-vdc-case__related-num" aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="dz-vdc-case__related-name">{r.title}</span>
                <span className="dz-vdc-case__related-kicker">{r.kicker}</span>
                <span className="dz-vdc-case__related-arrow" aria-hidden="true">→</span>
              </a>
            </li>
          ))}
        </ol>

      </div>
    </section>
  );
}


/* ─── Page ─── */
function VdcCasePage() {
  const data = window.DZ_VDC;
  const items = (data && data.projects && data.projects.items) || [];
  const path = (typeof window !== 'undefined') ? window.location.pathname : '';
  const match = path.match(/\/vdc\/([^/]+)\/?$/);
  const slug = match ? match[1] : '';
  const item = items.find((i) => i.slug === slug);

  if (!item || !item.detail) {
    return (
      <main className="dz-vdc-case dz-vdc-case--missing">
        <div className="dz-vdc-case__missing-inner">
          <span className="dz-vdc-case__missing-eyebrow">404</span>
          <h1 className="dz-vdc-case__missing-title">{dzT('Case not found', 'الحالة غير موجودة')}</h1>
          <p className="dz-vdc-case__missing-sub">
            {dzT("This case study doesn’t exist or hasn’t been published yet.", 'دراسة الحالة هذه غير موجودة أو لم تُنشَر بعد.')}
          </p>
          <a href={dzVdcHref()} className="dz-vdc-case__missing-back">
            <span aria-hidden="true">←</span>
            <span>{dzT('Back to all immersive work', 'العودة إلى كل الأعمال الغامرة')}</span>
          </a>
        </div>
      </main>
    );
  }

  const detail = item.detail;
  const showGallery = item.media && item.media.type === 'gallery';

  return (
    <main className="dz-vdc-case" id="dz-main" aria-labelledby="vdc-case-title">
      <VdcCaseProgress />
      <VdcCaseHero item={item} detail={detail} />

      <div className="dz-vdc-case__inner">

        {/* ── The brief ── */}
        {detail.storyParas && detail.storyParas.length ? (
          <VdcCaseReveal className="dz-vdc-case__story">
            <h2 className="dz-vdc-case__section-title" id="vdc-case-title">{dzT('The brief', 'الفكرة')}</h2>
            {detail.storyParas.map((p, i) => (
              <p key={i} className="dz-vdc-case__story-para">{p}</p>
            ))}
          </VdcCaseReveal>
        ) : null}

        {/* ── Watch it in action ──
              Two sources supported:
                • detail.caseVideo  → self-hosted mp4, native HTML5 controls
                                      (play/pause, sound, scrubber, fullscreen)
                • detail.youtubeId  → YouTube iframe (its own player chrome)
              caseVideo wins if both are set. controlsList="nodownload"
              hides the download button so visitors can't one-click rip
              the file (UI-only — not a real DRM). */}
        {(detail.caseVideo || detail.youtubeId) ? (
          <VdcCaseReveal className="dz-vdc-case__video">
            <h2 className="dz-vdc-case__section-title">{dzT('Watch it in action', 'شاهدها أثناء العمل')}</h2>
            <div className="dz-vdc-case__video-wrap">
              {detail.caseVideo ? (
                <video
                  className="dz-vdc-case__video-iframe"
                  src={detail.caseVideo}
                  poster={detail.caseVideoPoster || detail.heroImage || undefined}
                  controls
                  preload="metadata"
                  playsInline
                  controlsList="nodownload"
                  aria-label={item.title + ' — full project video'}
                />
              ) : (
                <iframe
                  className="dz-vdc-case__video-iframe"
                  src={'https://www.youtube.com/embed/' + detail.youtubeId + '?rel=0'}
                  title={item.title + ' — project video'}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              )}
            </div>
          </VdcCaseReveal>
        ) : null}

        {/* ── Key facts ── */}
        {detail.keyFacts && detail.keyFacts.length ? (
          <VdcCaseReveal className="dz-vdc-case__facts">
            <h2 className="dz-vdc-case__section-title">{dzT('Key facts', 'حقائق أساسية')}</h2>
            <ul className="dz-vdc-case__facts-grid">
              {detail.keyFacts.map((f, i) => (

                <li key={i} className="dz-vdc-case__fact"
                    style={{ transitionDelay: (i * 80) + 'ms' }}>
                  <span className="dz-vdc-case__fact-label">{f.label}</span>
                  <span className="dz-vdc-case__fact-value">{f.value}</span>
                </li>

              ))}
            </ul>
          </VdcCaseReveal>
        ) : null}

        {/* ── Approach (numbered scope) ── */}
        {detail.scope && detail.scope.length ? (
          <VdcCaseReveal className="dz-vdc-case__scope">
            <h2 className="dz-vdc-case__section-title">{dzT('Our approach', 'منهجنا')}</h2>
            <ol className="dz-vdc-case__scope-list">
              {detail.scope.map((b, i) => (
                <li key={i} className="dz-vdc-case__scope-item"
                    style={{ transitionDelay: (i * 100) + 'ms' }}>
                  <span className="dz-vdc-case__scope-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="dz-vdc-case__scope-text">{b}</span>
                </li>
              ))}
            </ol>
          </VdcCaseReveal>
        ) : null}

        {/* ── Gallery (Al Arabi only) ── */}
        {showGallery ? (
          <VdcCaseReveal className="dz-vdc-case__gallery-section">
            <h2 className="dz-vdc-case__section-title">{dzT('Themes explored', 'المحاور المُستكشَفة')}</h2>
            <p className="dz-vdc-case__lead">
              {dzT(
                'Each theme was rebuilt at full scale with PBR materials and dynamic lighting. Visitors swap between them mid-walkthrough.',
                'أُعيد بناء كل طراز بمقياسٍ كامل بمواد PBR وإضاءة ديناميكية، ويُبدّل الزوّار بينها في منتصف الجولة.'
              )}
            </p>
            <VdcCaseGallery shots={item.media.shots} />
          </VdcCaseReveal>
        ) : null}

        {/* ── Tools / hardware ── */}
        {detail.tools && detail.tools.length ? (
          <VdcCaseReveal className="dz-vdc-case__tools">
            <h2 className="dz-vdc-case__section-title">{dzT('Tools & hardware', 'الأدوات والعتاد')}</h2>
            <ul className="dz-vdc-case__tools-list">
              {detail.tools.map((t, i) => (
                <li key={i} className="dz-vdc-case__tool"
                    style={{ transitionDelay: (i * 80) + 'ms' }}>
                  <span className="dz-vdc-case__tool-label">{t.label}</span>
                  {t.note ? (
                    <span className="dz-vdc-case__tool-note">{t.note}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </VdcCaseReveal>
        ) : null}

        {/* ── Software stack ── */}
        {detail.software && detail.software.length ? (
          <VdcCaseReveal className="dz-vdc-case__software">
            <h2 className="dz-vdc-case__section-title">{dzT('Software stack', 'حزمة البرامج')}</h2>
            <div className="dz-vdc-case__software-chips">
              {detail.software.map((s) => (
                <span key={s} className="dz-vdc-case__chip dz-vdc-case__chip--soft">{s}</span>
              ))}
            </div>
          </VdcCaseReveal>
        ) : null}

      </div>

      <VdcCaseRelated all={items} currentSlug={item.slug} />
      <VdcCaseFloatingCta />
    </main>
  );
}

window.VdcCasePage = VdcCasePage;
